package org.example.project.enginekalender

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import kotlinx.browser.window
import org.example.project.dashboard.DashboardTheme
import org.example.project.dashboard.PopupEngineCard
import org.example.project.enginechat.CompanyHeaderRow
import org.example.project.enginechat.ChatContactsData
import org.example.project.enginechat.chatIdentityUserId
import org.example.project.enginechat.chatUserShortPrimaryLine
import org.example.project.enginechat.canSeeKontaktProfil
import org.example.project.enginechat.globalChatUserId
import org.example.project.enginechat.isCurrentChatUser
import org.example.project.enginechat.userDisplayName
import org.example.project.enginetabular.TableStyles
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.json.Json
import org.example.project.manager.LumaticaApiManager
import org.example.project.service.LoginService
import org.example.project.service.ZustandManager
import org.example.project.settings.AppDropdown
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.settings.LumaticaCheckbox
import org.example.project.screens_shared.taskParagraphLines
import org.example.project.utils.All
import org.example.project.utils.AufgabeZeile
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Style
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.TextArea

private fun participantSelectionKey(user: All): String =
    "${user.firmenId.trim()}:${chatIdentityUserId(user.id)}"

private fun parseStoredSelectionKeys(raw: String): Set<String> =
    raw.split(",").map { it.trim() }.filter { it.isNotBlank() }.toSet()

private val kalenderPopupContactsJson = Json {
    ignoreUnknownKeys = true
    encodeDefaults = false
}

private fun kalenderPopupApiPart(value: String): String =
    js("encodeURIComponent")(if (value.isBlank()) "~" else value) as String

private suspend fun loadKalenderPopupContactUsers(firmenId: String): List<All> {
    if (firmenId.isBlank()) return emptyList()
    val raw = runCatching {
        LumaticaApiManager.call("kontakte/users/${kalenderPopupApiPart(firmenId)}")
    }.getOrDefault("")
    if (raw.isBlank()) return emptyList()
    return runCatching { kalenderPopupContactsJson.decodeFromString<List<All>>(raw) }.getOrDefault(emptyList())
}

private suspend fun loadKalenderPopupGlobalCompanies(currentFirmenId: String): List<All> {
    val raw = runCatching {
        LumaticaApiManager.call("kontakte/global/${kalenderPopupApiPart(currentFirmenId)}")
    }.getOrDefault("")
    if (raw.isBlank()) return emptyList()
    return runCatching { kalenderPopupContactsJson.decodeFromString<List<All>>(raw) }.getOrDefault(emptyList())
}

private suspend fun loadKalenderPopupContactData(currentUserId: String): ChatContactsData {
    val currentUser = LoginService.getUser()
    val currentFirmenId = currentUser?.firmenId.orEmpty().trim()
    val currentChatUserId = globalChatUserId(currentFirmenId, currentUser?.id.orEmpty())
    val internalCompanies = if (currentFirmenId.isBlank()) {
        emptyList()
    } else {
        val internalUsers = loadKalenderPopupContactUsers(currentFirmenId)
            .asSequence()
            .filter { it.id.isNotBlank() }
            .filterNot { isCurrentChatUser(it, currentFirmenId, currentUserId, currentChatUserId) }
            .filter { it.firmenId == currentFirmenId }
            .filter { canSeeKontaktProfil(currentFirmenId, it) }
            .map { it.copy(id = globalChatUserId(it.firmenId, it.id)) }
            .sortedBy { userDisplayName(it).lowercase() }
            .toList()
        val companyLabel = internalUsers.firstOrNull()?.firma
            .orEmpty()
            .ifBlank { internalUsers.firstOrNull()?.kunde.orEmpty() }
            .ifBlank { currentUser?.kunde.orEmpty() }
        listOf(
            All(
                firmenId = currentFirmenId,
                firma = companyLabel,
                kunde = companyLabel,
                columnSettings = internalUsers
            )
        )
    }

    val externalCompanies = loadKalenderPopupGlobalCompanies(currentFirmenId).mapNotNull { company ->
        val companyId = company.firmenId.trim()
        if (companyId.isBlank() || companyId == currentFirmenId) return@mapNotNull null
        val users = company.columnSettings
            .filterNot { isCurrentChatUser(it, currentFirmenId, currentUserId, currentChatUserId) }
            .sortedBy { userDisplayName(it).lowercase() }
        if (users.isEmpty()) null else company.copy(columnSettings = users)
    }

    val companies = (internalCompanies + externalCompanies).filter { it.columnSettings.isNotEmpty() }
    val users = companies.flatMap { it.columnSettings }
    return ChatContactsData(
        contactCompanies = companies,
        allUsers = users.associateBy { it.id } + listOfNotNull(currentUser).associateBy {
            globalChatUserId(it.firmenId, it.id)
        }
    )
}

@Composable
fun TerminBookingPopup(
    popupState: All,
    art: String,
    serviceBookingMode: Boolean = false,
    providerFirmenId: String = "",
    providerUserId: String = "",
    allowedFrom: String,
    allowedTo: String,
    blockedBookings: List<All> = emptyList(),
    showBlockedBookingDetails: Boolean = true,
    onClose: () -> Unit,
    onSave: (All) -> Unit,
    saving: Boolean = false,
    saveError: String = "",
    taskMode: Boolean = false,
    onDeleteTask: (() -> Unit)? = null
) {
    val akquiseBooking = popupState.akquiseProspectId.isNotBlank()
    val akquiseTypes = listOf("Call", "Termin")
    Style(TableStyles)
    val currentUser = LoginService.getUser()
    val currentUserId = currentUser?.id.orEmpty().trim()
    val currentFirmenId = currentUser?.firmenId.orEmpty().trim()
    val fixedProviderMode = providerUserId.isNotBlank() &&
        providerFirmenId.isNotBlank() &&
        (providerUserId != currentUserId || providerFirmenId != currentFirmenId)
    val scope = rememberCoroutineScope()

    var titel by remember(popupState) {
        mutableStateOf(if (taskMode) popupState.bezeichnung else popupState.bezeichnung.ifBlank { popupState.art.ifBlank { "Termin" } })
    }
    var taskDate by remember(popupState) { mutableStateOf(popupState.datum) }
    var aufgabeZeilen by remember(popupState) {
        mutableStateOf(
            popupState.aufgabeZeilen.ifEmpty {
                taskParagraphLines(popupState.beschreibung.orEmpty())
                    .mapIndexed { index, label -> AufgabeZeile(id = "legacy-$index", label = label) }
                    .ifEmpty { listOf(AufgabeZeile(label = popupState.bezeichnung)) }
            }
        )
    }
    var contactCompanies by remember(popupState.id, art) { mutableStateOf<List<All>>(emptyList()) }
    var expandedCompanies by remember(popupState.id, art) { mutableStateOf(setOf<String>()) }
    var selectedTeilnehmerKeys by remember(popupState, providerUserId, serviceBookingMode, fixedProviderMode) {
        mutableStateOf(
            parseStoredSelectionKeys(popupState.teilnehmerAuswahl).ifEmpty {
                setOf("${currentFirmenId}:${popupState.kundeUserId.ifBlank { currentUserId }}")
            }
        )
    }
    var von by remember(popupState) { mutableStateOf(popupState.uhrzeitVon.ifBlank { "08:00" }) }
    var bis by remember(popupState) { mutableStateOf(popupState.uhrzeitBis.ifBlank { "09:00" }) }
    var ortText by remember(popupState) { mutableStateOf(if (akquiseBooking) "Online" else popupState.ort) }
    var ortOptions by remember(popupState.id, serviceBookingMode, fixedProviderMode) { mutableStateOf<List<All>>(emptyList()) }
    var selectedOrtKey by remember(popupState, serviceBookingMode, fixedProviderMode) { mutableStateOf("") }
    var freitext by remember(popupState) { mutableStateOf(popupState.beschreibung.orEmpty()) }
    var terminArt by remember(popupState) { mutableStateOf(if (akquiseBooking) popupState.terminArt else popupState.terminArt.ifBlank { "Meeting" }) }
    var teilnehmerZugangFilter by remember(popupState) { mutableStateOf("Alle") }
    val filteredContactCompanies = remember(contactCompanies, teilnehmerZugangFilter) {
        contactCompanies.mapNotNull { company ->
            val users = company.columnSettings.filter { user ->
                when (teilnehmerZugangFilter) {
                    "Desktop" -> user.zugangDesktop
                    "Mobil" -> user.zugangMobil
                    else -> true
                }
            }
            if (users.isEmpty()) null else company.copy(columnSettings = users)
        }
    }

    val selfKontakt = currentUser?.copy(
        id = currentUserId,
        firmenId = currentFirmenId,
        firma = currentUser.firma.ifBlank { currentUser.kunde },
        kunde = currentUser.kunde.ifBlank { currentUser.firma }
    )

    LaunchedEffect(art, currentUserId, akquiseBooking) {
        if (akquiseBooking || taskMode) return@LaunchedEffect
        val chatContacts = loadKalenderPopupContactData(currentUserId = currentUserId)
        val companies = chatContacts.contactCompanies.toMutableList()
        val selfCompanyId = currentFirmenId
        val selfCompanyLabel = currentUser?.firma
            .orEmpty()
            .ifBlank { currentUser?.kunde.orEmpty() }
            .ifBlank { selfCompanyId }
        if (selfCompanyId.isNotBlank()) {
            val index = companies.indexOfFirst { it.firmenId.trim() == selfCompanyId }
            if (index >= 0) {
                val company = companies[index]
                val hasSelf = company.columnSettings.any { chatIdentityUserId(it.id) == currentUserId }
                if (!hasSelf && selfKontakt != null) {
                    companies[index] = company.copy(columnSettings = listOf(selfKontakt) + company.columnSettings)
                }
            } else if (selfKontakt != null) {
                companies.add(
                    0,
                    All(
                        firmenId = selfCompanyId,
                        firma = selfCompanyLabel,
                        kunde = selfCompanyLabel,
                        columnSettings = listOf(selfKontakt)
                    )
                )
            }
        }
        contactCompanies = companies
        expandedCompanies = companies.map { it.firmenId.ifBlank { it.id } }.toSet()
        if (selectedTeilnehmerKeys.isEmpty() && currentUserId.isNotBlank() && currentFirmenId.isNotBlank()) {
            selectedTeilnehmerKeys = setOf("${currentFirmenId}:${currentUserId}")
        }
    }

    val participantMap = remember(contactCompanies) {
        buildMap<String, All> {
            contactCompanies.forEach { company ->
                company.columnSettings.forEach { user ->
                    val rawUserId = chatIdentityUserId(user.id)
                    if (rawUserId.isNotBlank() && user.firmenId.isNotBlank()) {
                        put(participantSelectionKey(user.copy(id = rawUserId)), user.copy(id = rawUserId))
                    }
                }
            }
        }
    }
    val selectedTeilnehmer = selectedTeilnehmerKeys.mapNotNull { participantMap[it] }
    val primaryExternalParticipant = selectedTeilnehmer.firstOrNull { it.firmenId.trim() != currentFirmenId }
    val primaryInternalParticipant = selectedTeilnehmer.firstOrNull { it.firmenId.trim() == currentFirmenId }
        ?: selfKontakt
    val shouldPersistAsServiceBooking = serviceBookingMode || fixedProviderMode || primaryExternalParticipant != null

    LaunchedEffect(currentFirmenId, providerFirmenId, primaryExternalParticipant, serviceBookingMode, fixedProviderMode) {
        if (akquiseBooking || taskMode) return@LaunchedEffect
        val otherFirmenId = when {
            serviceBookingMode || fixedProviderMode -> providerFirmenId
            primaryExternalParticipant != null -> primaryExternalParticipant.firmenId.trim()
            else -> currentFirmenId
        }.trim()
        ortOptions = if (currentFirmenId.isNotBlank() && otherFirmenId.isNotBlank()) {
            loadServiceBookingLocations(currentFirmenId, otherFirmenId)
        } else {
            emptyList()
        }
    }

    LaunchedEffect(ortOptions, ortText) {
        val initial = ortOptions.firstOrNull { it.ort == ortText && it.ort.isNotBlank() }
            ?: ortOptions.firstOrNull()
        selectedOrtKey = initial?.key.orEmpty()
        if (ortText.isBlank()) ortText = initial?.ort.orEmpty()
    }

    val selectedOrt = ortOptions.firstOrNull { it.key == selectedOrtKey }
    val withinAllowedSlots = isWithinAllowedBookingRange(von, bis, allowedFrom, allowedTo)
    val conflictCandidate = popupState.copy(uhrzeitVon = von, uhrzeitBis = bis)
    val conflictingBooking = conflictingKalenderBooking(blockedBookings, conflictCandidate)
    val canSave = if (taskMode) {
        taskDate.matches(Regex("\\d{4}-\\d{2}-\\d{2}")) &&
            isQuarterHourRange(von, bis) &&
            isWithinAllowedBookingRange(von, bis, allowedFrom, allowedTo) &&
            titel.isNotBlank() &&
            aufgabeZeilen.any { it.label.isNotBlank() } &&
            aufgabeZeilen.all { it.id.isBlank() || it.label.isNotBlank() } &&
            aufgabeZeilen.all { it.soll == null || it.soll!! > 0 }
    } else popupState.datum.isNotBlank() &&
        titel.isNotBlank() &&
        (if (akquiseBooking) terminArt in akquiseTypes else selectedTeilnehmerKeys.isNotEmpty()) &&
        isQuarterHourRange(von, bis) &&
        ortText.isNotBlank() &&
        withinAllowedSlots &&
        conflictingBooking == null

    val terminArtOptions = listOf("Meeting", "Schulung", "Beratung", "Sonstiges")
        .map { All(key = it, label = it) }
    val mobile = window.innerWidth <= 640
    val popupWidth = if (mobile) window.innerWidth else minOf(760, window.innerWidth - 24)
    fun currentTask(): All = popupState.copy(
        datum = taskDate,
        uhrzeitVon = von,
        uhrzeitBis = bis,
        bezeichnung = titel.trim(),
        beschreibung = popupState.beschreibung,
        aufgabeTyp = "TEXT",
        aufgabeZeilen = aufgabeZeilen.filter { it.label.isNotBlank() }
    )

    PopupEngineCard(
        windowKey = if (taskMode) "aufgaben-booking-popup" else if (mobile) "termin-booking-mobile-fixed" else "termin-booking-popup",
        widthPx = popupWidth,
        heightPx = if (mobile) window.innerHeight else minOf(780, window.innerHeight - 24),
        minWidthPx = if (mobile) 0 else 320,
        minHeightPx = if (mobile) 0 else 420,
        initialX = if (mobile) 0.0 else ((window.innerWidth - popupWidth) / 2.0),
        initialY = if (mobile) 0.0 else 12.0,
        frameBackground = DashboardTheme.POPUP_BG,
        contentBackground = DashboardTheme.POPUP_BG,
        resizable = !mobile,
        viewportAnchored = true,
        onClose = { if (!saving) onClose() }
    ) {
        Div({
            style {
                padding(18.px)
                boxSizing("border-box")
                height(100.percent)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                overflow("hidden")
            }
        }) {
            Div({
                style {
                    width(100.percent)
                    height(100.percent)
                    property("background", DashboardTheme.POPUP_BG)
                    property("border", "1px solid transparent")
                    borderRadius(18.px)
                    boxSizing("border-box")
                    padding(24.px)
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(18.px)
                    overflowY("auto")
                }
            }) {
                if (!taskMode) {
                    Span({
                        style {
                            color(Color.white)
                            fontSize(24.px)
                            fontWeight("700")
                            textAlign("center")
                            width(100.percent)
                        }
                    }) { Text("Termin Buchen") }

                    TerminField("Titel") {
                        Input(InputType.Text, attrs = {
                            value(titel)
                            style { terminInputStyle() }
                            onInput { titel = it.value }
                        })
                    }
                }

                if (taskMode) {
                    Div({ style { width(100.percent) } }) {
                        Input(InputType.Text, attrs = {
                            value(titel)
                            attr("placeholder", "Aufgabe")
                            style { terminInputStyle() }
                            onInput { titel = it.value }
                        })
                    }
                    TerminField("Aufgabe") {
                        Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(8.px); width(100.percent) } }) {
                            aufgabeZeilen.forEachIndexed { index, zeile ->
                                Div({ style {
                                    display(DisplayStyle.Grid)
                                    property("grid-template-columns", "minmax(0, 1fr) 110px")
                                    gap(8.px); width(100.percent)
                                } }) {
                                    Input(InputType.Text, attrs = {
                                        value(zeile.label)
                                        attr("placeholder", "Aufgabe")
                                        style { terminInputStyle(); minWidth(0.px) }
                                        onInput { event ->
                                            aufgabeZeilen = aufgabeZeilen.toMutableList().also {
                                                it[index] = it[index].copy(label = event.value)
                                            }
                                        }
                                    })
                                    Input(InputType.Number, attrs = {
                                        value(zeile.soll?.toString().orEmpty())
                                        attr("placeholder", "Soll")
                                        attr("min", "1")
                                        style { terminInputStyle(); minWidth(0.px) }
                                        onInput { event ->
                                            val soll = event.value?.toString()?.toIntOrNull()?.takeIf { it > 0 }
                                            aufgabeZeilen = aufgabeZeilen.toMutableList().also {
                                                it[index] = it[index].copy(soll = soll)
                                            }
                                        }
                                    })
                                }
                                if (zeile.erledigt || zeile.ist > 0) {
                                    Span({ style { color(Color("#9ad7a7")); fontSize(12.px) } }) {
                                        Text("${if (zeile.erledigt) "✓  " else ""}${zeile.ist}${zeile.soll?.let { " / $it" }.orEmpty()}")
                                    }
                                }
                            }
                            LumaticaButton("+ Aufgabe hinzufügen", onClick = {
                                if (aufgabeZeilen.size < 50) aufgabeZeilen = aufgabeZeilen + AufgabeZeile()
                            }, enabled = aufgabeZeilen.size < 50, variant = LumaticaButtonVariant.Standard)
                        }
                    }
                } else if (akquiseBooking) {
                    TerminField("Typ") {
                        if (terminArt.isNotBlank()) {
                            val typLabel = when (terminArt) {
                                "Call" -> if (popupState.bezeichnung.startsWith("Follow-up")) "Follow-up" else "Call"
                                "Termin" -> "Termin"
                                else -> terminArt
                            }
                            Div({ style { display(DisplayStyle.Flex); property("width", "fit-content"); property("align-self", "flex-start") } }) {
                                LumaticaButton(
                                    typLabel,
                                    onClick = {},
                                    variant = LumaticaButtonVariant.State,
                                    isActive = true,
                                    enabled = false,
                                    disabledVisualOpacity = 1.0
                                )
                            }
                        } else {
                            Div({ style { display(DisplayStyle.Flex); gap(10.px) } }) {
                                akquiseTypes.forEach { type ->
                                    LumaticaButton(type, {
                                        if (titel == popupState.bezeichnung || titel == terminArt+" \u00b7 "+popupState.firma) {
                                            titel = type+" \u00b7 "+popupState.firma
                                        }
                                        terminArt = type
                                    }, variant = LumaticaButtonVariant.State, isActive = terminArt == type, enabled = !saving)
                                }
                            }
                        }
                    }
                } else {
                TerminField("Teilnehmer") {
                    Div({
                        classes(TableStyles.tableScrollFrame)
                        style {
                            flex("none")
                            width(100.percent)
                            height(180.px)
                            minHeight(150.px)
                            maxHeight(360.px)
                            overflowY("auto")
                            overflowX("hidden")
                            property("resize", "vertical")
                            backgroundColor(Color("rgba(23, 26, 32, 0.38)"))
                            borderRadius(10.px)
                            padding(8.px, 10.px)
                            boxSizing("border-box")
                            border {
                                width(1.px)
                                style(LineStyle.Solid)
                                color(Color("#303645"))
                            }
                            display(DisplayStyle.Flex)
                            flexDirection(FlexDirection.Column)
                            gap(6.px)
                        }
                    }) {
                        Span({
                            style {
                                color(DashboardTheme.TEXT_MUTED)
                                fontSize(12.px)
                                fontWeight("600")
                            }
                        }) { Text("${selectedTeilnehmerKeys.size} Teilnehmer markiert") }
                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                gap(8.px)
                                flexWrap(FlexWrap.Wrap)
                            }
                        }) {
                            Span({
                                style {
                                    color(DashboardTheme.TEXT_MUTED)
                                    fontSize(12.px)
                                    fontWeight("600")
                                }
                            }) { Text("Zugangstyp") }
                            listOf("Alle", "Desktop", "Mobil").forEach { option ->
                                LumaticaButton(
                                    label = option,
                                    variant = LumaticaButtonVariant.State,
                                    isActive = teilnehmerZugangFilter == option,
                                    heightPx = 28,
                                    paddingH = 10,
                                    onClick = { teilnehmerZugangFilter = option }
                                )
                            }
                        }

                        if (filteredContactCompanies.isEmpty()) {
                            Span({
                                style {
                                    color(DashboardTheme.TEXT_LIGHT)
                                    fontSize(12.px)
                                }
                            }) { Text("Keine Kontakte vorhanden.") }
                        } else {
                            filteredContactCompanies.forEach { company ->
                                val companyId = company.firmenId.ifBlank { company.id }
                                val companyLabel = company.firma.ifBlank { company.kunde.ifBlank { companyId.ifBlank { "Firma" } } }
                                val expanded = expandedCompanies.contains(companyId)
                                CompanyHeaderRow(
                                    label = companyLabel,
                                    onClick = {
                                        expandedCompanies = if (expanded) expandedCompanies - companyId else expandedCompanies + companyId
                                    }
                                )
                                if (expanded) {
                                    company.columnSettings.forEach { user ->
                                        val rawUserId = chatIdentityUserId(user.id)
                                        val normalizedUser = user.copy(id = rawUserId)
                                        val key = participantSelectionKey(normalizedUser)
                                        val checked = selectedTeilnehmerKeys.contains(key)
                                        Div({
                                            style {
                                                padding(8.px, 12.px, 8.px, 14.px)
                                                cursor("pointer")
                                                display(DisplayStyle.Flex)
                                                alignItems(AlignItems.Center)
                                                gap(8.px)
                                            }
                                            onClick {
                                                selectedTeilnehmerKeys =
                                                    if (checked) selectedTeilnehmerKeys - key else selectedTeilnehmerKeys + key
                                            }
                                        }) {
                                            LumaticaCheckbox(
                                                checked = checked,
                                                onChange = { isChecked ->
                                                    selectedTeilnehmerKeys =
                                                        if (isChecked) selectedTeilnehmerKeys + key else selectedTeilnehmerKeys - key
                                                },
                                                fontSizePx = 14
                                            )
                                            Img(src = "images/user.svg", attrs = {
                                                style {
                                                    width(18.px)
                                                    height(18.px)
                                                    property("filter", "brightness(0) invert(1)")
                                                    flexShrink(0)
                                                }
                                            })
                                            Span({
                                                style {
                                                    fontSize(12.px)
                                                    color(Color.white)
                                                    property("overflow", "hidden")
                                                    property("text-overflow", "ellipsis")
                                                    property("white-space", "nowrap")
                                                }
                                            }) {
                                                Text(chatUserShortPrimaryLine(normalizedUser))
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }

                }

                if (!taskMode && !akquiseBooking && shouldPersistAsServiceBooking) {
                    TerminField("Termin Art") {
                        AppDropdown(
                            options = terminArtOptions,
                            selectedValue = terminArt,
                            onChange = { terminArt = it },
                            placeholder = "Termin Art"
                        )
                    }
                }

                Div({
                    style {
                        display(DisplayStyle.Flex)
                        gap(12.px)
                        alignItems(AlignItems.FlexEnd)
                    }
                }) {
                    Div({ style { flex(2) } }) {
                        TerminField("Zeitfenster") {
                            if (taskMode) Input(InputType.Date, attrs = {
                                value(taskDate)
                                style { terminInputStyle() }
                                onInput { taskDate = it.value }
                            }) else Input(InputType.Text, attrs = {
                                value(formatGermanDate(popupState.datum))
                                attr("readonly", "true")
                                style { terminInputStyle() }
                            })
                        }
                    }
                    Div({ style { flex(1) } }) {
                        TerminField("Start") {
                            Input(InputType.Time, attrs = {
                                value(von)
                                attr("step", "900")
                                style { terminInputStyle() }
                                onInput { von = it.value }
                            })
                        }
                    }
                    Div({ style { flex(1) } }) {
                        TerminField("Ende") {
                            Input(InputType.Time, attrs = {
                                value(bis)
                                attr("step", "900")
                                style { terminInputStyle() }
                                onInput { bis = it.value }
                            })
                        }
                    }
                }

                if (!withinAllowedSlots) {
                    Span({
                        style {
                            color(DashboardTheme.TEXT_MUTED)
                            fontSize(13.px)
                            fontWeight("600")
                        }
                    }) { Text("Buchbar nur von $allowedFrom bis $allowedTo") }
                }

                if (!taskMode) conflictingBooking?.let { conflict ->
                    Span({
                        style {
                            color(Color("#fca5a5"))
                            fontSize(13.px)
                            fontWeight("700")
                        }
                    }) {
                        if (showBlockedBookingDetails) {
                            val label = conflict.bezeichnung
                                .ifBlank { conflict.abwesenheitsart }
                                .ifBlank { conflict.art }
                                .ifBlank { "Termin" }
                            Text("Zeitfenster blockiert: $label ${conflict.uhrzeitVon} - ${conflict.uhrzeitBis}")
                        } else {
                            Text("Zeitfenster nicht verfügbar")
                        }
                    }
                }

                if (!taskMode) TerminField("Ort") {
                    if (akquiseBooking) {
                        Input(InputType.Text, attrs = { value("Online"); attr("readonly", "true"); style { terminInputStyle() } })
                    } else if (ortOptions.isNotEmpty()) {
                        AppDropdown(
                            options = ortOptions,
                            selectedValue = selectedOrtKey,
                            onChange = { key ->
                                selectedOrtKey = key
                                ortText = ortOptions.firstOrNull { it.key == key }?.ort.orEmpty()
                            },
                            placeholder = "Ort auswählen"
                        )
                    } else {
                        Input(InputType.Text, attrs = {
                            value(ortText)
                            style { terminInputStyle() }
                            onInput { ortText = it.value }
                        })
                    }
                }

                if (!taskMode) TerminField("Ihre Nachricht") {
                    TextArea(attrs = {
                        value(freitext)
                        style {
                            terminInputStyle()
                            if (akquiseBooking) { backgroundColor(Color("#3F454E")); color(Color.white) }
                            minHeight(108.px)
                            padding(12.px)
                        }
                        onInput { freitext = it.value }
                    })
                }

                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.FlexEnd)
                        gap(10.px)
                        paddingTop(4.px)
                    }
                }) {
                    if (saveError.isNotBlank()) Span({ attr("role", "alert") }) { Text(saveError) }
                    if (taskMode && onDeleteTask != null) {
                        LumaticaButton("Löschen", onClick = onDeleteTask, enabled = !saving,
                            variant = LumaticaButtonVariant.Standard)
                    }
                    LumaticaButton("Abbrechen", onClick = onClose, enabled = !saving, variant = LumaticaButtonVariant.Standard)
                    LumaticaButton(
                        if (saving) "Wird gespeichert ..." else "Speichern",
                        onClick = {
                            scope.launch {
                                if (taskMode) {
                                    onSave(currentTask())
                                    return@launch
                                }
                                if (akquiseBooking) {
                                    onSave(popupState.copy(art = "Termin", bezeichnung = titel, terminArt = terminArt,
                                        kontakt = popupState.kontakt.ifBlank { currentUserId }, erstelltVon = currentUserId,
                                        uhrzeitVon = von, uhrzeitBis = bis, ort = "Online", ortTyp = "Online",
                                        beschreibung = freitext, belegt = true, zustand = ZustandManager.Termin.ANGEMELDET,
                                        serviceTermin = false, teilnehmerAuswahl = "", teilnehmerUserIds = "", teilnehmerAnzeige = "",
                                        zusatzTeilnehmer = "", empfaenger = "", meetings = "",
                                        anbieterFirmenId = "", anbieterUserId = "", kundeFirmenId = "", kundeUserId = ""))
                                    return@launch
                                }
                                val providerParticipant = when {
                                    fixedProviderMode || serviceBookingMode -> All(id = providerUserId, firmenId = providerFirmenId)
                                    primaryExternalParticipant != null -> primaryExternalParticipant
                                    else -> null
                                }
                                val customerParticipant = when {
                                    fixedProviderMode || serviceBookingMode -> primaryInternalParticipant
                                    primaryExternalParticipant != null -> primaryInternalParticipant
                                    else -> primaryInternalParticipant
                                } ?: selfKontakt ?: All(id = currentUserId, firmenId = currentFirmenId)

                                val additionalParticipants = selectedTeilnehmer.filterNot { participant ->
                                    participant.firmenId.trim() == providerParticipant?.firmenId.orEmpty() &&
                                        participant.id == providerParticipant?.id.orEmpty()
                                }.filterNot { participant ->
                                    participant.firmenId.trim() == customerParticipant.firmenId.trim() &&
                                        participant.id == customerParticipant.id
                                }

                                val savedBooking = popupState.copy(
                                    art = "Termin",
                                    bezeichnung = titel,
                                    kontakt = if (shouldPersistAsServiceBooking) providerParticipant?.id.orEmpty() else popupState.kontakt,
                                    empfaenger = if (shouldPersistAsServiceBooking) providerParticipant?.id.orEmpty() else customerParticipant.id,
                                    ansprechpartner = userDisplayName(customerParticipant),
                                    ort = ortText,
                                    uhrzeitVon = von,
                                    uhrzeitBis = bis,
                                    beschreibung = freitext,
                                    meetings = "",
                                    belegt = true,
                                    zustand = popupState.zustand.takeIf { it in listOf(
                                        ZustandManager.Termin.ANGEMELDET,
                                        ZustandManager.Termin.ZUGESAGT,
                                        ZustandManager.Termin.STATTGEFUNDEN,
                                        ZustandManager.Termin.STORNIERT
                                    ) } ?: ZustandManager.Termin.ANGEMELDET,
                                    terminArt = terminArt,
                                    ortTyp = selectedOrt?.ortTyp.orEmpty(),
                                    anbieterFirmenId = providerParticipant?.firmenId.orEmpty(),
                                    anbieterUserId = providerParticipant?.id.orEmpty(),
                                    kundeFirmenId = customerParticipant.firmenId.trim().ifBlank { currentFirmenId },
                                    kundeUserId = customerParticipant.id,
                                    serviceTermin = shouldPersistAsServiceBooking,
                                    teilnehmerUserIds = selectedTeilnehmer
                                            .filter { it.firmenId.trim() == currentFirmenId }
                                            .map { it.id }
                                            .distinct()
                                            .joinToString(","),
                                    zusatzTeilnehmer = additionalParticipants
                                            .map { "${it.firmenId.trim()}:${it.id}" }
                                            .distinct()
                                            .joinToString(","),
                                    teilnehmerAnzeige = selectedTeilnehmer
                                            .map { "${userDisplayName(it)} - ${it.firma.ifBlank { it.kunde.ifBlank { it.firmenId } }}" }
                                            .distinct()
                                            .joinToString(" | "),
                                    teilnehmerAuswahl = selectedTeilnehmerKeys.joinToString(",")
                                )
                                onSave(savedBooking)
                            }
                        },
                        variant = LumaticaButtonVariant.Action,
                        enabled = canSave && !saving
                    )
                }
            }
        }
    }
}
